package com.evoapps.evoshift.ui.reports

import com.evoapps.evoshift.util.usesDecimalComma
import com.evoapps.evoshift.domain.market.payroll.MonthPayFactors
import com.evoapps.evoshift.domain.market.payroll.PayLineKind
import com.evoapps.evoshift.domain.model.AbsenceType
import com.evoapps.evoshift.domain.model.AdjustmentType
import com.evoapps.evoshift.domain.model.EmployeePayroll
import com.evoapps.evoshift.domain.model.RosterPayroll
import com.evoapps.evoshift.domain.model.SystemContractType
import com.evoapps.evoshift.domain.model.WorkingTimeSheet
import com.evoapps.evoshift.domain.model.TimesheetHoursSource
import com.evoapps.evoshift.domain.model.TimesheetReportSettings
import com.evoapps.evoshift.domain.model.WeekdayFormat
import com.evoapps.evoshift.ui.finance.formatDecimal
import com.evoapps.evoshift.ui.finance.formatMoney
import kotlinx.datetime.DayOfWeek
import kotlinx.datetime.LocalDate

/** Texts of the printed reports, resolved in the reader's language (ReportPrintViewModel). */
data class ReportLabels(
    val language: String,
    val workingTimeTitle: String,
    val payrollTitle: String,
    val payslipTitle: String,
    val employee: String,
    val workplace: String,
    val role: String,
    val month: String,
    val contract: String,
    val colDate: String,
    val colDay: String,
    val colPlanned: String,
    val colClocked: String,
    val colHours: String,
    val colNight: String,
    val colNotes: String,
    val sunday: String,
    val holiday: String,
    val plannedOnly: String,
    val totalHours: String,
    val norm: String,
    val nightHours: String,
    val sundayHolidayHours: String,
    val absences: String,
    val signEmployee: String,
    val signEmployer: String,
    val generated: String,
    val gross: String,
    val toPay: String,
    val employerCost: String,
    val deductedFromPay: String,
    val paidByEmployer: String,
    val bonuses: String,
    val deductions: String,
    val refunds: String,
    val hoursWorked: String,
    val total: String,
    val disclaimer: String,
    /** SH-46: the summary's plan vs actual table. */
    val planVsActual: String,
    val plannedHours: String,
    val employerCostPlan: String,
    val monthName: (Int) -> String,
    val dayShort: (DayOfWeek) -> String,
    val absence: (AbsenceType) -> String,
    val contractName: (SystemContractType) -> String,
    val line: (PayLineKind) -> String,
    /**
     * SH-57: the overtime, Sunday and holiday and minimum-wage lines of a person's month, as label
     * to amount (the same lines as the labour cost screen's).
     */
    val payFactorLines: (MonthPayFactors, String) -> List<Pair<String, String>> = { _, _ -> emptyList() },
    /** SH-251: the employer in the working-time record's header (shown when it is known). */
    val employer: String = "Employer",
    /** SH-258: the day's full name ("Wtorek"), for the records that print it so. */
    val dayFull: (DayOfWeek) -> String = dayShort,
    /** SH-258: the column with an empty cell per day for the manager's signature. */
    val colManagerSignature: String = "Manager's signature",
    /** SH-258: the note under a record whose times are rounded ("Times rounded to 15 minutes ..."), or null. */
    val roundingNote: (TimesheetReportSettings) -> String? = { null }
) {
    val polish: Boolean get() = usesDecimalComma(language)
    fun hours(value: Double) = if (polish) formatDecimal(value, 2, ' ', ',') else formatDecimal(value, 2)
    fun money(value: Double, currency: String) = formatMoney(value, currency)

    /** "2026-02-15" as "15.02" (the year is the report's month). */
    fun shortDate(isoDate: String): String = runCatching { LocalDate.parse(isoDate) }.getOrNull()
        ?.let { "${it.day.toString().padStart(2, '0')}.${(it.month.ordinal + 1).toString().padStart(2, '0')}" } ?: isoDate
}

/** Builds the printable A4 documents; pure, so the content is tested without printing. */
object ReportHtml {

    /**
     * The working-time records, one sheet (page) per person and workplace. [employers]: the
     * employer's name by workplace id, where it is known (the owner's business name).
     */
    fun workingTime(sheets: List<WorkingTimeSheet>, labels: ReportLabels, employers: Map<String, String> = emptyMap()): String = document(
        title = labels.workingTimeTitle,
        language = labels.language,
        body = sheets.joinToString("\n") { sheet(it, labels, employers[it.workplaceId]?.takeIf(String::isNotBlank)) }
    )

    /** SH-258: a column of the working-time record's day table. */
    internal enum class Column(val widthMm: Double, val flex: Boolean = false) {
        DATE(12.0), DAY_SHORT(11.0), DAY_FULL(20.0),
        PLAN(24.0, flex = true), CLOCKED(24.0, flex = true),
        HOURS(16.0), NIGHT(14.0),
        NOTES(20.0, flex = true), SIGNATURE(18.0)
    }

    /**
     * SH-258: the record's columns by the workplace's choices: the date, the day of the week, the
     * plan and the clock-in times by the hours shown, the hours, the night hours, the remarks and
     * the manager's signature. The defaults are SH-251's columns.
     */
    internal fun columns(options: TimesheetReportSettings): List<Column> = listOfNotNull(
        Column.DATE,
        when (options.weekday) {
            WeekdayFormat.NONE -> null
            WeekdayFormat.SHORT -> Column.DAY_SHORT
            WeekdayFormat.FULL -> Column.DAY_FULL
        },
        Column.PLAN.takeIf { options.hoursSource != TimesheetHoursSource.ACTUAL },
        Column.CLOCKED.takeIf { options.hoursSource != TimesheetHoursSource.PLAN },
        Column.HOURS,
        Column.NIGHT,
        Column.NOTES.takeIf { options.showRemarks },
        Column.SIGNATURE.takeIf { options.showManagerSignature }
    )

    /**
     * SH-258: the columns' weights across the page (190 mm), for the settings' preview: the fixed
     * ones as set, the times and the remarks share the rest.
     */
    internal fun columnWidthsMm(columns: List<Column>): List<Double> {
        val fixed = columns.filterNot { it.flex }.sumOf { it.widthMm }
        val weights = columns.filter { it.flex }.sumOf { it.widthMm }
        val rest = TABLE_MM - fixed
        return columns.map { if (it.flex) rest * it.widthMm / weights else it.widthMm }
    }

    /** SH-258: the day table's widths at one type size, and how many shifts share a line of the time columns. */
    internal data class Layout(val widthsMm: List<Double>, val shiftsPerLine: Int)

    /**
     * SH-258: the widths at [fontPt]. The time columns take what their longest day needs (two
     * shifts' times on a line, "07:55–16:10, 18:00–23:00": about 15 em in WebKit, and the
     * padding), the remarks the rest, so they are not cut short; only when that would leave the
     * remarks under [MIN_NOTES_MM] do the times give up the second shift on a line. Without the
     * remarks the time columns share the rest.
     */
    internal fun layout(columns: List<Column>, sheet: WorkingTimeSheet?, fontPt: Double): Layout {
        val times = columns.count { it == Column.PLAN || it == Column.CLOCKED }
        val fixed = columns.filterNot { it.flex }.sumOf { it.widthMm }
        val rest = TABLE_MM - fixed
        val two = TWO_SHIFTS_EM * fontPt * PT_MM + CELL_PADDING_MM
        if (Column.NOTES !in columns || times == 0) {
            val time = if (times == 0) 0.0 else rest / times
            return Layout(columns.map { if (it.flex) time else it.widthMm }, if (time >= two) 2 else 1)
        }
        val split = sheet == null || sheet.days.any { it.shifts.size >= 2 }
        val want = if (split) two else ONE_SHIFT_EM * fontPt * PT_MM + CELL_PADDING_MM
        val (time, perLine) = if (rest - times * want >= MIN_NOTES_MM) want to 2
            else (rest - MIN_NOTES_MM) / times to (if ((rest - MIN_NOTES_MM) / times >= two) 2 else 1)
        val notes = rest - times * time
        return Layout(columns.map { if (it == Column.NOTES) notes else if (it.flex) time else it.widthMm }, perLine)
    }

    /** SH-258: [layout] at the default size (as the tests and the settings ask "would two shifts share a line"). */
    internal fun shiftsPerLine(columns: List<Column>, fontPt: Double = PrintFit.MAX_PT): Int = layout(columns, null, fontPt).shiftsPerLine

    /**
     * SH-258: the lines [text] takes in a cell [widthMm] wide at [fontPt], wrapped at spaces as the
     * print engines do (about 0.6 em a character in WebKit's bold, with a little to spare); a word
     * longer than the line breaks within it.
     */
    internal fun textLines(text: String, widthMm: Double, fontPt: Double): Int {
        if (text.isEmpty()) return 1
        val perLine = maxOf(1, ((widthMm - CELL_PADDING_MM) / (CHAR_EM * fontPt * PT_MM)).toInt())
        var lines = 1
        var used = 0
        for (word in text.split(' ')) {
            val length = word.length
            when {
                used == 0 -> { lines += (length - 1) / perLine; used = (length - 1) % perLine + 1 }
                used + 1 + length <= perLine -> used += 1 + length
                else -> { lines += 1 + (length - 1) / perLine; used = (length - 1) % perLine + 1 }
            }
        }
        return lines
    }

    /** The remarks of [day]: holiday, Sunday, absence. */
    private fun remarks(day: com.evoapps.evoshift.domain.model.WorkingTimeDay, l: ReportLabels): String = listOfNotNull(
        l.holiday.takeIf { day.isHoliday },
        l.sunday.takeIf { day.isSunday && !day.isHoliday },
        day.absence?.let(l.absence)
    ).joinToString(", ")

    /**
     * SH-207/SH-251: one A4 page per person, the type sized to the month's lines (two shifts
     * share a line of the time columns where they fit at that size): the header row, a row per
     * day and the summary rows of the same grid. SH-258: the manager's signature column's header
     * takes two lines, the rounding note one more line under the table, and with [l] the remarks
     * and the absences' summary wrap instead of being cut short (their lines counted).
     */
    internal fun workingTimeFit(sheet: WorkingTimeSheet, rounded: Boolean = sheet.options.rounds, l: ReportLabels? = null): PrintFit.Fit {
        val columns = columns(sheet.options)
        val notes = columns.indexOf(Column.NOTES)
        val hours = columns.indexOf(Column.HOURS)
        return PrintFit.fit(fixedMm = WORKING_TIME_FIXED_MM + if (rounded) NOTE_MM else 0.0) { size ->
            val layout = layout(columns, sheet, size)
            val perLine = layout.shiftsPerLine
            val summary = l?.let { workingTimeSummary(sheet, it) }
            listOf(if (Column.SIGNATURE in columns) 2 else 1) +
                sheet.days.map { day ->
                    val times = maxOf(1, (day.shifts.size + perLine - 1) / perLine)
                    val remark = if (l == null || notes < 0) 1 else textLines(remarks(day, l), layout.widthsMm[notes], size)
                    maxOf(times, remark)
                } +
                List(WORKING_TIME_SUMMARY_ROWS) { i ->
                    val line = summary?.getOrNull(i)
                    if (line == null || line.number) 1 else textLines(line.value, layout.widthsMm.drop(hours).sum(), size)
                }
        }
    }

    /** SH-207: the payroll summary's two tables (a row per person, the header and the total). */
    internal fun payrollSummaryFit(people: Int): PrintFit.Fit =
        PrintFit.fit(rowLines = List(2 * (people + 2)) { 1 }, fixedMm = PAYROLL_FIXED_MM)

    /** A summary line of the working-time record: [number] values sit right-aligned in the hours column. */
    internal data class SummaryLine(val label: String, val value: String, val number: Boolean = true)

    /**
     * The summary of [sheet] in the order printed (SH-251: each value next to its own label, in
     * one row of the day table's grid).
     */
    internal fun workingTimeSummary(sheet: WorkingTimeSheet, l: ReportLabels): List<SummaryLine> = listOf(
        SummaryLine(l.totalHours, l.hours(sheet.totalHours)),
        SummaryLine(l.norm, l.hours(sheet.normHours.toDouble())),
        SummaryLine(l.nightHours, l.hours(sheet.nightHours)),
        SummaryLine(l.sundayHolidayHours, l.hours(sheet.sundayAndHolidayHours)),
        SummaryLine(
            l.absences,
            sheet.absenceDays.entries.joinToString(", ") { (type, days) -> "${l.absence(type)}: $days" }.ifEmpty { "–" },
            number = false
        )
    )

    private fun sheet(sheet: WorkingTimeSheet, l: ReportLabels, employer: String?): String = buildString {
        val fit = workingTimeFit(sheet, rounded = sheet.options.rounds && l.roundingNote(sheet.options) != null, l = l)
        val month = "${l.monthName(sheet.month)} ${sheet.year}"
        append("<section class=\"page sheet\" style=\"${fit.style}\">")
        append("<div class=\"head\"><h1>${esc(l.workingTimeTitle)}</h1><div class=\"period\">${esc(month)}</div></div>")
        // The month heads the page; below it the person and their norm, then where they work.
        append(meta(listOfNotNull(
            l.employee to sheet.employeeName,
            l.role to (sheet.role ?: "–"),
            l.norm to "${l.hours(sheet.normHours.toDouble())} h",
            employer?.let { l.employer to it },
            l.workplace to sheet.workplaceName
        )))
        val o = sheet.options
        val columns = columns(o)
        val widths = layout(columns, sheet, fit.fontPt).widthsMm
        append("<table class=\"days\"><colgroup>")
        widths.forEach { append("<col style=\"width:${percent(it)}%\">") }
        append("</colgroup><thead><tr>")
        columns.forEach { column ->
            val label = when (column) {
                Column.DATE -> l.colDate
                Column.DAY_SHORT, Column.DAY_FULL -> l.colDay
                Column.PLAN -> l.colPlanned
                Column.CLOCKED -> l.colClocked
                Column.HOURS -> l.colHours
                Column.NIGHT -> l.colNight
                Column.NOTES -> l.colNotes
                Column.SIGNATURE -> l.colManagerSignature
            }
            val cls = when (column) {
                Column.HOURS, Column.NIGHT -> " class=\"num\""
                Column.SIGNATURE -> " class=\"wrap\""
                else -> ""
            }
            append("<th$cls>${esc(label)}</th>")
        }
        append("</tr></thead><tbody>")
        // Plan-only rows are marked only where the record mixes registered and planned hours.
        val marksPlanned = o.hoursSource == TimesheetHoursSource.COMBINED
        sheet.days.forEachIndexed { index, day ->
            val date = LocalDate.parse(day.date)
            val rest = day.isSunday || day.isHoliday
            val saturday = date.dayOfWeek == DayOfWeek.SATURDAY
            // Sundays and holidays darker, Saturdays lighter, other days zebra: greys that stay
            // apart in black-and-white print.
            val classes = listOfNotNull(
                "rest".takeIf { rest },
                "saturday".takeIf { saturday && !rest },
                "zebra".takeIf { !rest && !saturday && index % 2 == 1 },
                "absent".takeIf { day.absence != null }
            )
            append("<tr${if (classes.isEmpty()) "" else " class=\"${classes.joinToString(" ")}\""}>")
            columns.forEach { column ->
                when (column) {
                    Column.DATE -> append("<td>${date.day.toString().padStart(2, '0')}.${date.month.ordinal.plus(1).toString().padStart(2, '0')}</td>")
                    Column.DAY_SHORT -> append("<td>${esc(l.dayShort(date.dayOfWeek))}</td>")
                    Column.DAY_FULL -> append("<td class=\"day\">${esc(l.dayFull(date.dayOfWeek))}</td>")
                    // Two shifts share a line where they fit; a time itself never breaks.
                    Column.PLAN -> append("<td class=\"time\">${day.shifts.joinToString(", ") { time(span(it.plannedStart, it.plannedEnd)) }}</td>")
                    // An unclocked shift counts the planned hours: "–*", explained by the footnote.
                    Column.CLOCKED -> append("<td class=\"time\">${day.shifts.joinToString(", ") { s -> time(if (s.clocked) span(s.actualStart!!, s.actualEnd!!) else if (marksPlanned) "–*" else "–") }}</td>")
                    Column.HOURS -> append("<td class=\"num\">${if (day.shifts.isEmpty()) "" else l.hours(day.hours)}</td>")
                    Column.NIGHT -> append("<td class=\"num\">${if (day.nightHours > 0) l.hours(day.nightHours) else ""}</td>")
                    Column.NOTES -> append("<td class=\"notes\">${esc(remarks(day, l))}</td>")
                    Column.SIGNATURE -> append("<td class=\"sign\"></td>")
                }
            }
            append("</tr>")
        }
        append("</tbody><tbody class=\"summary\">")
        // SH-251: the summary is part of the same grid: the label over the columns before the
        // hours, the value in the hours column, so a value cannot slip next to another label.
        val before = columns.indexOf(Column.HOURS)
        val after = columns.size - before - 1
        workingTimeSummary(sheet, l).forEachIndexed { i, line ->
            append("<tr${if (i == 0) " class=\"first\"" else ""}><td class=\"label\" colspan=\"$before\">${esc(line.label)}</td>")
            if (line.number) append("<td class=\"num\">${esc(line.value)}</td>${if (after > 0) "<td colspan=\"$after\"></td>" else ""}")
            else append("<td class=\"text\" colspan=\"${after + 1}\">${esc(line.value)}</td>")
            append("</tr>")
        }
        append("</tbody></table>")
        if (marksPlanned && sheet.days.any { d -> d.shifts.any { !it.clocked } }) append("<p class=\"note\">* ${esc(l.plannedOnly)}</p>")
        if (o.rounds) l.roundingNote(o)?.let { append("<p class=\"note\">${esc(it)}</p>") }
        append(signatures(l.signEmployee, l.signEmployer))
        append("<p class=\"footer\">${esc(l.generated)}</p>")
        append("</section>")
    }

    /** The month's payroll: a summary page and a payslip per person, or only [onlyUserId]'s payslip. */
    fun payroll(payroll: RosterPayroll, workplaceName: String, labels: ReportLabels, onlyUserId: String? = null): String {
        val l = labels
        val employees = payroll.employees.filter { onlyUserId == null || it.userId == onlyUserId }
        val month = "${l.monthName(payroll.month)} ${payroll.year}"
        val body = buildString {
            if (onlyUserId == null) {
                // SH-207: the summary's two tables on one page as far as the type allows.
                val fit = payrollSummaryFit(employees.size)
                append("<section class=\"page\" style=\"${fit.style}\"><h1>${esc(l.payrollTitle)}</h1>")
                append(meta(listOf(l.workplace to workplaceName, l.month to month)))
                append("<table class=\"days\"><thead><tr>")
                listOf(l.employee, l.contract, l.hoursWorked, l.gross, l.deductedFromPay, l.toPay, l.employerCost)
                    .forEach { append("<th>${esc(it)}</th>") }
                append("</tr></thead><tbody>")
                employees.forEach { e ->
                    append("<tr><td class=\"name\">${esc(e.employeeName)}</td><td>${esc(contract(e, l))}</td>")
                    append("<td class=\"num\">${l.hours(e.paidHours)}</td>")
                    append("<td class=\"num\">${l.money(e.payslip.gross, payroll.currency)}</td>")
                    append("<td class=\"num\">${l.money(e.payslip.employeeLines.sumOf { it.amount }, payroll.currency)}</td>")
                    append("<td class=\"num\"><b>${l.money(e.toPay, payroll.currency)}</b></td>")
                    append("<td class=\"num\">${l.money(e.employerCost, payroll.currency)}</td></tr>")
                }
                append("<tr class=\"sum\"><td colspan=\"3\">${esc(l.total)}</td>")
                append("<td class=\"num\">${l.money(payroll.totalGross, payroll.currency)}</td><td></td>")
                append("<td class=\"num\"><b>${l.money(payroll.totalToPay, payroll.currency)}</b></td>")
                append("<td class=\"num\">${l.money(payroll.totalEmployerCost, payroll.currency)}</td></tr>")
                append("</tbody></table>")
                // SH-46: the rostered hours and their employer cost against the actual ones.
                append("<h2>${esc(l.planVsActual)}</h2><table class=\"days\"><thead><tr>")
                listOf(l.employee, l.plannedHours, l.hoursWorked, l.employerCostPlan, l.employerCost)
                    .forEach { append("<th>${esc(it)}</th>") }
                append("</tr></thead><tbody>")
                employees.forEach { e ->
                    append("<tr><td class=\"name\">${esc(e.employeeName)}</td>")
                    append("<td class=\"num\">${l.hours(e.plannedHours)}</td><td class=\"num\">${l.hours(e.paidHours)}</td>")
                    append("<td class=\"num\">${l.money(e.plannedEmployerCost, payroll.currency)}</td>")
                    append("<td class=\"num\">${l.money(e.employerCost, payroll.currency)}</td></tr>")
                }
                append("<tr class=\"sum\"><td>${esc(l.total)}</td>")
                append("<td class=\"num\">${l.hours(payroll.totalPlannedHours)}</td><td class=\"num\">${l.hours(payroll.totalPaidHours)}</td>")
                append("<td class=\"num\">${l.money(payroll.totalPlannedEmployerCost, payroll.currency)}</td>")
                append("<td class=\"num\">${l.money(payroll.totalEmployerCost, payroll.currency)}</td></tr>")
                append("</tbody></table>")
                append("<p class=\"note\">${esc(l.disclaimer)}</p>")
                append("<p class=\"footer\">${esc(l.generated)}</p></section>")
            }
            employees.forEach { append(payslip(it, workplaceName, month, payroll.currency, l)) }
        }
        return document(l.payrollTitle, l.language, body)
    }

    private fun payslip(e: EmployeePayroll, workplaceName: String, month: String, currency: String, l: ReportLabels) = buildString {
        val slip = e.payslip
        append("<section class=\"page payslip\"><h1>${esc(l.payslipTitle)}</h1>")
        append(meta(listOf(l.employee to e.employeeName, l.workplace to workplaceName, l.month to month, l.contract to contract(e, l))))
        append("<table class=\"totals\">")
        append(row(l.hoursWorked, l.hours(e.paidHours)))
        append(row(l.gross, l.money(slip.gross, currency), bold = true))
        if (e.bonuses > 0) append(row("&nbsp;&nbsp;${esc(l.bonuses)}", l.money(e.bonuses, currency), raw = true))
        append(adjustmentLines(e, AdjustmentType.BONUS, "", currency, l))
        e.factors?.let { factors ->
            l.payFactorLines(factors, currency).forEach { (label, amount) -> append(row("&nbsp;&nbsp;${esc(label)}", amount, raw = true)) }
        }
        append("<tr><th colspan=\"2\">${esc(l.deductedFromPay)}</th></tr>")
        slip.employeeLines.forEach { append(row("&nbsp;&nbsp;${esc(l.line(it.kind))}", l.money(it.amount, currency), raw = true)) }
        if (e.deductions > 0) append(row(l.deductions, "−" + l.money(e.deductions, currency)))
        append(adjustmentLines(e, AdjustmentType.DEDUCTION, "−", currency, l))
        if (e.expenseRefunds > 0) append(row(l.refunds, "+" + l.money(e.expenseRefunds, currency)))
        append(adjustmentLines(e, AdjustmentType.EXPENSE_REFUND, "+", currency, l))
        append(row(l.toPay, l.money(e.toPay, currency), bold = true))
        if (slip.employerLines.isNotEmpty()) {
            append("<tr><th colspan=\"2\">${esc(l.paidByEmployer)}</th></tr>")
            slip.employerLines.forEach { append(row("&nbsp;&nbsp;${esc(l.line(it.kind))}", l.money(it.amount, currency), raw = true)) }
        }
        append(row(l.employerCost, l.money(e.employerCost, currency), bold = true))
        append("</table>")
        append(signatures(l.signEmployee, l.signEmployer))
        append("<p class=\"footer\">${esc(l.generated)}</p></section>")
    }

    /** SH-78: each bonus, deduction or refund of [type] on its own line: "15.02 · Valentine's Day  100,00 zł". */
    private fun adjustmentLines(e: EmployeePayroll, type: AdjustmentType, sign: String, currency: String, l: ReportLabels) =
        e.adjustments.filter { it.type == type }.joinToString("") { a ->
            row("&nbsp;&nbsp;&nbsp;&nbsp;${esc(l.shortDate(a.date))} · ${esc(a.description)}", sign + l.money(a.amount, currency), raw = true)
        }

    private fun contract(e: EmployeePayroll, l: ReportLabels) =
        if (e.contract == SystemContractType.NOT_SET) "–" else l.contractName(e.contract)

    private fun time(text: String) = "<span class=\"t\">$text</span>"

    private fun span(start: String, end: String) = "${start.substringAfter('T').take(5)}–${end.substringAfter('T').take(5)}"

    private fun meta(items: List<Pair<String, String>>) = items.joinToString("", "<dl class=\"meta\">", "</dl>") { (k, v) ->
        "<div><dt>${esc(k)}</dt><dd>${esc(v)}</dd></div>"
    }

    /**
     * Besides the table rows: the title, the two meta lines (a long name may take two lines),
     * the footnote, the signatures and the footer (millimetres, from the stylesheet's fixed
     * sizes, with a little to spare).
     */
    private const val WORKING_TIME_FIXED_MM = 64.0
    private const val WORKING_TIME_SUMMARY_ROWS = 5
    /** SH-258: a note line under the table (7 pt and its margin). */
    private const val NOTE_MM = 4.5
    /** A4 less the 10 mm margins: the day table's width. */
    private const val TABLE_MM = 190.0
    /** SH-258: two shifts' times on one line ("07:55–16:10, 18:00–23:00") in em, with room for the wider system fonts of WebKit. */
    private const val TWO_SHIFTS_EM = 15.0
    /** SH-258: one shift's times ("07:55–16:10") in em, as [TWO_SHIFTS_EM]. */
    private const val ONE_SHIFT_EM = 7.5
    /** SH-258: the narrowest the remarks get before the times give up room. */
    private const val MIN_NOTES_MM = 22.0
    /** SH-258: a character of the remarks in em (WebKit's bold, with a little to spare). */
    private const val CHAR_EM = 0.6
    private const val PT_MM = 25.4 / 72
    /** The cells' padding left and right (1.4 mm each). */
    private const val CELL_PADDING_MM = 2.8

    /** [mm] of the table's width as a CSS percentage ("24.15"). */
    private fun percent(mm: Double): String {
        val hundredths = kotlin.math.round(mm / TABLE_MM * 10000).toLong()
        return "${hundredths / 100}.${(hundredths % 100).toString().padStart(2, '0')}"
    }
    private const val PAYROLL_FIXED_MM = 48.0

    private fun row(label: String, value: String, bold: Boolean = false, raw: Boolean = false): String {
        val name = if (raw) label else esc(label)
        val shown = if (bold) "<b>${esc(value)}</b>" else esc(value)
        return "<tr><td>$name</td><td class=\"num\">$shown</td></tr>"
    }

    /** SH-251: two signature lines side by side, each labelled under its line. */
    private fun signatures(employee: String, employer: String) =
        "<table class=\"signatures\"><tr><td>${esc(employee)}</td><td class=\"gap\"></td><td>${esc(employer)}</td></tr></table>"

    fun esc(text: String) = text.replace("&", "&amp;").replace("<", "&lt;").replace(">", "&gt;").replace("\"", "&quot;")

    private fun document(title: String, language: String, body: String) = """
        <!doctype html>
        <html lang="$language"><head><meta charset="utf-8"><title>${esc(title)}</title>
        <style>
        @page { size: A4 portrait; margin: 10mm; }
        * { box-sizing: border-box; }
        html, body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        body { font-family: "Noto Sans", -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif; font-size: 9pt; color: #111; margin: 0; }
        .page { page-break-after: always; break-after: page; font-size: var(--fs, 9pt); }
        .page:last-child { page-break-after: auto; break-after: auto; }
        .payslip { page-break-inside: avoid; }
        h1 { font-size: 14pt; line-height: 1.2; color: #002B49; margin: 0 0 2mm; }
        h2 { font-size: 11pt; line-height: 1.2; margin: 1mm 0 2mm; }
        dl.meta { display: grid; grid-template-columns: minmax(0, 1.5fr) minmax(0, 1.5fr) minmax(0, 1fr) minmax(0, 0.8fr); gap: 0 4mm; margin: 0 0 3mm; }
        .sheet .head { display: flex; justify-content: space-between; align-items: baseline; border-bottom: 0.75pt solid #002B49; margin: 0 0 2mm; }
        .sheet .head h1 { margin: 0 0 1mm; }
        .sheet .period { font-size: 11pt; line-height: 1.2; font-weight: 600; color: #002B49; }
        .sheet dl.meta { grid-template-columns: minmax(0, 1.5fr) minmax(0, 1fr) minmax(0, 1.3fr); gap: 1.5mm 4mm; }
        .sheet dd { white-space: normal; overflow-wrap: anywhere; }
        th.num { text-align: right; }
        tbody.summary td { background: #fff; }
        tbody.summary tr.first td { border-top: 1.2pt solid #002B49; }
        tbody.summary td.label, tbody.summary td.num { font-weight: 600; }
        tbody.summary td.text { white-space: normal; overflow-wrap: anywhere; }
        dt { font-size: 7pt; line-height: 1.2; color: #444; text-transform: uppercase; letter-spacing: .03em; }
        dd { margin: 0; font-size: 9pt; line-height: 1.3; font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        table { width: 100%; border-collapse: collapse; margin-bottom: 3mm; table-layout: fixed; }
        th, td { border: 0.5pt solid #8a8a8a; padding: var(--pad, 1mm) 1.4mm; vertical-align: top; line-height: 1.3; overflow: hidden; }
        th { background: #002B49; color: #fff; text-align: left; font-weight: 600; white-space: nowrap; text-overflow: ellipsis; }
        td.num { text-align: right; white-space: nowrap; font-variant-numeric: tabular-nums; }
        td.time { font-variant-numeric: tabular-nums; }
        span.t { white-space: nowrap; }
        td.name, td.day { white-space: nowrap; text-overflow: ellipsis; }
        td.notes { white-space: normal; overflow-wrap: anywhere; }
        th.wrap { white-space: normal; }
        tr.zebra td { background: #f2f2f2; }
        tr.saturday td { background: #e4e4e4; }
        tr.rest td { background: #cfcfcf; }
        tr.absent td.notes { font-weight: 600; }
        tr.sum td { font-weight: 600; background: #e4e4e4; }
        table.totals { width: 60%; }
        table.totals td { padding: var(--pad, 1mm) 1.4mm; }
        .note { font-size: 7pt; line-height: 1.2; color: #444; margin: 0 0 2mm; }
        table.signatures { margin: 12mm 0 0; }
        table.signatures td { border: 0; border-top: 0.5pt solid #333; padding: 1mm 0 0; font-size: 8pt; line-height: 1.3; color: #333; text-align: center; }
        table.signatures td.gap { width: 20%; border-top: 0; }
        .footer { font-size: 7pt; line-height: 1.2; color: #555; margin-top: 3mm; }
        </style></head>
        <body>
        $body
        </body></html>
    """.trimIndent()
}
